import type { TypedDocumentNode } from '@apollo/client';
import { gql, useLazyQuery } from '@apollo/client';
import { faPen, faRefresh } from '@fortawesome/pro-solid-svg-icons';
import { useState } from 'react';
import { FormattedMessage } from 'react-intl';
import { useSearchParams } from 'react-router';
import type { CSSProperties } from 'styled-components';
import styled from 'styled-components';

import {
  Position,
  Rarity,
  Sport,
} from '@sorare/core/src/__generated__/globalTypes';
import { FadeInOut } from '@sorare/core/src/atoms/animations/FadeInOut';
import { ButtonBase } from '@sorare/core/src/atoms/buttons/ButtonBase';
import { LoadingButton } from '@sorare/core/src/atoms/buttons/LoadingButton';
import { FontAwesomeIcon } from '@sorare/core/src/atoms/icons';
import { Horizontal, Vertical } from '@sorare/core/src/atoms/layout/flex';
import { Card } from '@sorare/core/src/components/card/Card';
import { Dialog } from '@sorare/core/src/components/dialog';
import { LineupCardsLayout } from '@sorare/core/src/components/lineup/LineupCardsLayout';
import { useIsTabletAndAbove } from '@sorare/core/src/hooks/device/useIsTabletAndAbove';
import { useMutation } from '@sorare/core/src/hooks/graphql/useMutation';
import { useMonetaryAmount } from '@sorare/core/src/hooks/useMonetaryAmount';
import { useEvents } from '@sorare/core/src/lib/events/useEvents';
import { glossary } from '@sorare/core/src/lib/glossary';
import { getSortedCards } from '@sorare/core/src/lib/players';

import { useCart } from '@sorare/marketplace/src/hooks/useCart';

import { EditIndividualPlayerDialog } from 'components/onboarding/FirstLineup/FirstLineupEdition/FirstLineupPreview/EditIndividualPlayerDialog';
import { FirstLineupPlayerPrice } from 'components/onboarding/FirstLineup/FirstLineupEdition/FirstLineupPreview/FirstLineupPlayerPrice';
import { useAddRecommendedLineupToCart } from 'hooks/onboarding/useAddRecommendedLineupToCart';

import type {
  EditFirstLineupDialog_anyCard,
  EditFirstLineupDialog_so5Leaderboard,
  addRecommendedLineupToCartShuffleMutation,
  addRecommendedLineupToCartShuffleMutationVariables,
  clearCartMutation,
  clearCartMutationVariables,
  editIndividualPlayerQuery,
  editIndividualPlayerQueryVariables,
} from './__generated__/index.graphql';

const Root = styled(Vertical).attrs({ gap: 3 })`
  position: relative;
  height: 100%;
  background:
    no-repeat center / cover var(--bg-url),
    var(--c-black);
  padding: var(--double-unit);
`;
const ContentWrapper = styled(Vertical)`
  flex: 1;
`;
const CloseButtonWrapper = styled.div`
  position: absolute;
  top: var(--double-unit);
  right: var(--double-unit);
`;

const CardWrapper = styled(Vertical).attrs({ center: true })`
  position: relative;
`;
const EditIconWrapper = styled(Horizontal).attrs({ center: true })`
  position: absolute;
  z-index: 1;
  right: calc(-1 * var(--unit));
  top: calc(-1 * var(--unit));
  width: var(--triple-unit);
  height: var(--triple-unit);
  border-radius: 50%;
  background-color: var(--c-black);
  border: 1px solid var(--c-nd-50);
`;

const anyCardFragment = gql`
  fragment EditFirstLineupDialog_anyCard on AnyCardInterface {
    slug
    anyPositions
    anyPlayer {
      slug
    }
    livePrimaryOffer {
      id
      price {
        ...useMonetaryAmount_monetaryAmount
      }
    }
    ...Card_anyCard
  }
  ${useMonetaryAmount.fragments.monetaryAmount}
  ${Card.fragments.anyCard}
` as TypedDocumentNode<EditFirstLineupDialog_anyCard>;

const EDIT_INDIVIDUAL_PLAYER_QUERY = gql`
  query editIndividualPlayerQuery($cardSlug: String!) {
    anyCard(slug: $cardSlug) {
      slug
      ...EditFirstLineupDialog_anyCard
    }
  }
  ${anyCardFragment}
` as TypedDocumentNode<
  editIndividualPlayerQuery,
  editIndividualPlayerQueryVariables
>;

const ADD_RECOMMENDED_CARDS_TO_CART_SHUFFLE_MUTATION = gql`
  mutation addRecommendedLineupToCartShuffleMutation(
    $input: addRecommendedLineupToCartInput!
  ) {
    addRecommendedLineupToCart(input: $input) {
      cart {
        id
        mainCartItems {
          id
          card {
            slug
            ...EditFirstLineupDialog_anyCard
          }
        }
      }
      errors {
        message
        code
      }
    }
  }
  ${anyCardFragment}
` as TypedDocumentNode<
  addRecommendedLineupToCartShuffleMutation,
  addRecommendedLineupToCartShuffleMutationVariables
>;

// Do not fetch the cart here to avoid empty page flash
const CLEAR_CART_MUTATION = gql`
  mutation clearCartMutation($removeFromCartInput: removeFromCartInput!) {
    removeFromCart(input: $removeFromCartInput) {
      errors {
        message
        code
      }
    }
  }
` as TypedDocumentNode<clearCartMutation, clearCartMutationVariables>;

type Props = {
  so5Leaderboard?: EditFirstLineupDialog_so5Leaderboard;
  cards: EditFirstLineupDialog_anyCard[];
  onCartUpdated: () => Promise<void>;
  cartQueryLoading?: boolean;
  backgroundUrl?: string | null;
  open?: boolean;
  onClose: () => void;
};
export const EditFirstLineupDialog = ({
  so5Leaderboard,
  cards: cardsProp,
  backgroundUrl,
  open,
  onCartUpdated,
  cartQueryLoading,
  onClose: onCloseProp,
}: Props) => {
  const isTabletAndAbove = useIsTabletAndAbove();
  const track = useEvents();
  const [selectedCard, setSelectedCard] =
    useState<EditFirstLineupDialog_anyCard | null>(null);
  const [searchParams] = useSearchParams();
  const teamSlugs = searchParams.get('teamSlugs')?.split(',');
  const [shuffleUsed, setShuffleUsed] = useState(false);
  const [editIndividualPlayerQuery] = useLazyQuery(
    EDIT_INDIVIDUAL_PLAYER_QUERY
  );
  const [initialCards] = useState(cardsProp);
  const [cards, setCards] =
    useState<EditFirstLineupDialog_anyCard[]>(cardsProp);
  const { cart, addCardsToCart } = useCart();

  const [addRecommendedLineupToCartMutation, { loading: shuffleLoading }] =
    useMutation(ADD_RECOMMENDED_CARDS_TO_CART_SHUFFLE_MUTATION, {
      showErrorsWithSnackNotification: true,
    });
  const [clearCart, { loading: clearCartLoading }] = useMutation(
    CLEAR_CART_MUTATION,
    {
      showErrorsWithSnackNotification: true,
    }
  );

  const addRecommendedLineupToCart =
    useAddRecommendedLineupToCart<addRecommendedLineupToCartShuffleMutation>({
      mutate: addRecommendedLineupToCartMutation,
    });

  if (!cards.length && cardsProp.length) {
    setCards(cardsProp);
  }

  const selectedCardIndex = cards.findIndex(
    card => card.slug === selectedCard?.slug
  );
  const onSelectNewPlayer = async ({
    cardSlug,
    playerSlug,
  }: {
    cardSlug: string;
    playerSlug: string;
  }) => {
    track('Swap Player In Edit First Lineup', {
      newPlayer: playerSlug,
      oldPlayer: selectedCard?.anyPlayer.slug || '',
    });
    const { data } = await editIndividualPlayerQuery({
      variables: {
        cardSlug,
      },
    });
    if (data?.anyCard) {
      setCards(prev => {
        const newCards = [...prev];
        newCards.splice(selectedCardIndex, 1, data.anyCard);
        return newCards;
      });
    }
    setSelectedCard(null);
  };

  const isExtra = selectedCardIndex === cards.length - 1;

  const selectedCardPositions = isExtra
    ? [Position.Defender, Position.Midfielder, Position.Forward]
    : cards[selectedCardIndex]?.anyPositions;

  const { toMonetaryAmount } = useMonetaryAmount();

  const onSubmit = async () => {
    const cardsHaveChanged = !cards.every(card =>
      initialCards.some(c => c.slug === card.slug)
    );

    if (cardsHaveChanged) {
      await clearCart({
        variables: {
          removeFromCartInput: {
            cartId: cart?.id || '',
            cartItemIds: cart?.mainCartItems.map(({ id }) => id) || [],
          },
        },
      });
      await addCardsToCart.mutate(
        Sport.FOOTBALL,
        cards.map(({ slug }) => slug)
      );
      await onCartUpdated();
    }
    onCloseProp();
  };

  const onClose = async () => {
    if (shuffleUsed) {
      /*
        When we shuffle the backend automatically adds the new cards to the cart, even if the user does not confirm the team.
        This is a workaround to remove the cards from the cart if the user does not confirm the team, until the backend exposes another way to get shuffled cards.
      */
      await clearCart({
        variables: {
          removeFromCartInput: {
            cartId: cart?.id || '',
            cartItemIds: cart?.mainCartItems.map(({ id }) => id) || [],
          },
        },
      });
      await addCardsToCart.mutate(
        Sport.FOOTBALL,
        initialCards.map(({ slug }) => slug)
      );
      await onCartUpdated();
    }
    onCloseProp();
  };

  return (
    <>
      <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm" hideHeader>
        {({ CloseButton }) => (
          <Root
            style={
              { '--bg-url': `url(${backgroundUrl || ''})` } as CSSProperties
            }
          >
            <CloseButtonWrapper>
              <CloseButton onClose={onClose} />
            </CloseButtonWrapper>
            <ContentWrapper>
              <LineupCardsLayout fillHeightPercent={0.6}>
                {cards.map(card => {
                  const price = toMonetaryAmount(card.livePrimaryOffer?.price);
                  return (
                    <FadeInOut key={card.slug}>
                      <ButtonBase onClick={() => setSelectedCard(card)}>
                        <CardWrapper>
                          <EditIconWrapper>
                            <FontAwesomeIcon icon={faPen} size="xs" />
                          </EditIconWrapper>
                          <Vertical>
                            <Card card={card} />
                            {!price.isZero && (
                              <FirstLineupPlayerPrice price={price} />
                            )}
                          </Vertical>
                        </CardWrapper>
                      </ButtonBase>
                    </FadeInOut>
                  );
                })}
              </LineupCardsLayout>
            </ContentWrapper>
            <Vertical center>
              <LoadingButton
                startIcon={
                  !shuffleLoading && <FontAwesomeIcon icon={faRefresh} />
                }
                loading={shuffleLoading}
                color="secondary"
                size="medium"
                onClick={async () => {
                  track('Click Shuffle In Edit First Lineup', {});
                  const { data } = await addRecommendedLineupToCart({
                    teamSlugs: teamSlugs || [],
                    so5LeaderboardSlug: so5Leaderboard?.slug || '',
                    rarity: Rarity.limited,
                  });
                  const recommendedCards =
                    data?.addRecommendedLineupToCart?.cart?.mainCartItems.map(
                      ({ card }) => card
                    );
                  if (recommendedCards) {
                    setShuffleUsed(true);
                    setCards(getSortedCards(recommendedCards));
                  }
                }}
              >
                <FormattedMessage {...glossary.shuffle} />
              </LoadingButton>
            </Vertical>
            <LoadingButton
              color="primary"
              size="medium"
              loading={
                !!clearCartLoading ||
                !!addCardsToCart.loading ||
                !!cartQueryLoading
              }
              onClick={onSubmit}
              fullWidth={!isTabletAndAbove}
            >
              <FormattedMessage
                id="EditFirstLineupDialog.cta"
                defaultMessage="Confirm Team"
              />
            </LoadingButton>
          </Root>
        )}
      </Dialog>

      {!!selectedCard && !!so5Leaderboard && (
        <EditIndividualPlayerDialog
          selectedPlayerSlug={selectedCard.anyPlayer.slug}
          so5Leaderboard={so5Leaderboard}
          selectedCardPositions={selectedCardPositions}
          onSelectNewPlayer={onSelectNewPlayer}
          onClose={() => setSelectedCard(null)}
          open
        />
      )}
    </>
  );
};

EditFirstLineupDialog.fragments = {
  anyCard: anyCardFragment,
  so5Leaderboard: gql`
    fragment EditFirstLineupDialog_so5Leaderboard on So5Leaderboard {
      slug
      ...EditIndividualPlayerDialog_so5Leaderboard
    }
    ${EditIndividualPlayerDialog.fragments.so5Leaderboard}
  ` as TypedDocumentNode<EditFirstLineupDialog_so5Leaderboard>,
};
